// frontend/src/components/common/BackendStatus.jsx
// ============================================================
// Backend health / wake-up probe.
// GET {API_BASE_URL}/health on first use. A request slower than 8 s or a
// 502/503 response means the host is cold-starting ("waking"). While not
// healthy we keep probing (backoff while waking, every 60 s otherwise).
// State lives in a tiny module store so the banner, the footer and any
// other consumer share one poller.
// ============================================================

import { useEffect, useSyncExternalStore } from "react";
import { API_ENDPOINTS } from "../../config";
import Spinner from "./Spinner";
import "./ui.css";

const SLOW_MS = 8000;
const TIMEOUT_MS = 45000;
const UNHEALTHY_INTERVAL_MS = 60000;

/** status: 'checking' | 'ok' | 'degraded' | 'waking' | 'down' */
let state = {
  status: "checking",
  version: null,
  services: null,
  lastChecked: null,
  latencyMs: null,
};
const listeners = new Set();
let started = false;
let inFlight = false;
let retryTimer = null;
let wakeAttempts = 0;

function setState(patch) {
  state = { ...state, ...patch };
  listeners.forEach((l) => l());
}

function schedule(ms) {
  clearTimeout(retryTimer);
  retryTimer = setTimeout(check, ms);
}

async function check() {
  if (inFlight) return;
  inFlight = true;
  clearTimeout(retryTimer);
  const started_at = performance.now();
  const controller = new AbortController();
  const slowTimer = setTimeout(() => setState({ status: "waking" }), SLOW_MS);
  const abortTimer = setTimeout(() => controller.abort(), TIMEOUT_MS);

  try {
    const res = await fetch(API_ENDPOINTS.health, {
      signal: controller.signal,
      headers: { Accept: "application/json" },
      cache: "no-store",
    });
    const latencyMs = Math.round(performance.now() - started_at);

    if (res.status === 502 || res.status === 503 || res.status === 504) {
      wakeAttempts += 1;
      setState({ status: "waking", lastChecked: Date.now(), latencyMs });
      schedule(Math.min(5000 * 2 ** (wakeAttempts - 1), UNHEALTHY_INTERVAL_MS));
      return;
    }
    if (!res.ok) {
      setState({ status: "down", lastChecked: Date.now(), latencyMs });
      schedule(UNHEALTHY_INTERVAL_MS);
      return;
    }

    let body = null;
    try {
      body = await res.json();
    } catch {
      body = null;
    }
    wakeAttempts = 0;
    const degraded = body?.status === "degraded";
    setState({
      status: degraded ? "degraded" : "ok",
      version: body?.version ?? null,
      services: body?.services ?? null,
      lastChecked: Date.now(),
      latencyMs,
    });
    if (degraded) schedule(UNHEALTHY_INTERVAL_MS);
  } catch {
    // Network error or timeout. A cold host often refuses briefly: treat the
    // first few failures as "waking", then report "down".
    wakeAttempts += 1;
    const waking = wakeAttempts <= 3;
    setState({ status: waking ? "waking" : "down", lastChecked: Date.now(), latencyMs: null });
    schedule(waking ? Math.min(5000 * 2 ** (wakeAttempts - 1), UNHEALTHY_INTERVAL_MS) : UNHEALTHY_INTERVAL_MS);
  } finally {
    clearTimeout(slowTimer);
    clearTimeout(abortTimer);
    inFlight = false;
  }
}

function subscribe(listener) {
  listeners.add(listener);
  if (!started && typeof window !== "undefined") {
    started = true;
    check();
  }
  return () => listeners.delete(listener);
}

const getSnapshot = () => state;

/** Force a fresh probe (e.g. after a failed API call). */
export function recheckBackend() {
  wakeAttempts = 0;
  check();
}

/**
 * const { status, isHealthy, isWaking, version, services, lastChecked, latencyMs, recheck } = useBackendStatus();
 */
export function useBackendStatus() {
  const snap = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  return {
    ...snap,
    isHealthy: snap.status === "ok" || snap.status === "degraded",
    isWaking: snap.status === "waking",
    recheck: recheckBackend,
  };
}

/** Slim top banner shown while the backend is waking up or unreachable. */
export default function BackendStatus() {
  const { status } = useBackendStatus();

  // Re-probe when the tab becomes visible again and we are not healthy.
  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === "visible" && (state.status === "down" || state.status === "waking")) {
        check();
      }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  const show = status === "waking" || status === "down";

  return (
    <div className={`backend-banner${show ? " is-visible" : ""}${status === "down" ? " is-down" : ""}`} role="status" aria-live="polite">
      {show && (
        <div className="backend-banner__inner">
          {status === "waking" ? (
            <>
              <Spinner size={12} label="Waiting for server" />
              <span>Waking the server up… this can take up to 30 seconds</span>
            </>
          ) : (
            <>
              <span className="backend-banner__dot" aria-hidden="true" />
              <span>We can&apos;t reach the server right now. Retrying automatically.</span>
              <button type="button" className="backend-banner__retry" onClick={recheckBackend}>
                Retry now
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
